// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Badge,
  FilterSelect,
  FiltersToolbar,
  formatDateTimeVn,
} from "@chotca/ui";
import type { AdminClaimInvitationItem } from "@chotca/config";
import { useAdminAllClaimInvitations } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { DataTable, usePagination, type Column } from "@/components/data-table";

type ClaimStatus = "PENDING" | "CLAIMED" | "REVOKED" | "EXPIRED";

function deriveStatus(row: AdminClaimInvitationItem): ClaimStatus {
  if (row.claimedAt) return "CLAIMED";
  if (row.revokedAt) return "REVOKED";
  if (new Date(row.expiresAt).getTime() <= Date.now()) return "EXPIRED";
  return "PENDING";
}

const STATUS_TONE = {
  PENDING: "sun",
  CLAIMED: "lime-soft",
  REVOKED: "danger-soft",
  EXPIRED: "smoke",
} as const;

export function ClaimInvitationsClient() {
  const t = useTranslations("admin");
  const filters = useTableFilters({ status: {} });
  const status = (filters.applied.status ?? "") as ClaimStatus | "";
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminAllClaimInvitations({
    ...(status ? { status } : {}),
    page,
    limit: pageSize,
  });
  const rows = (data?.items ?? []) as AdminClaimInvitationItem[];

  const columns: Column<AdminClaimInvitationItem>[] = [
    {
      key: "createdAt",
      header: t("claimsColCreated"),
      cell: (r) => (
        <span className="whitespace-nowrap text-xs">
          {formatDateTimeVn(new Date(r.createdAt))}
        </span>
      ),
    },
    {
      key: "org",
      header: t("claimsColOrg"),
      cell: (r) => (
        <Link
          to={`/orgs/${r.orgId}`}
          className="font-semibold text-ink hover:underline"
        >
          {r.orgName}
          <span className="ml-1 text-xs font-normal text-ash">
            · {r.orgDistrict}
          </span>
        </Link>
      ),
    },
    {
      key: "email",
      header: t("claimsColEmail"),
      cell: (r) => <span className="text-xs">{r.email}</span>,
    },
    {
      key: "expires",
      header: t("claimsColExpires"),
      cell: (r) => (
        <span className="whitespace-nowrap text-xs text-ash">
          {formatDateTimeVn(new Date(r.expiresAt))}
        </span>
      ),
    },
    {
      key: "status",
      header: t("claimsColStatus"),
      cell: (r) => {
        const s = deriveStatus(r);
        return (
          <Badge tone={STATUS_TONE[s]}>
            {t(
              `claimsStatus${s.charAt(0) + s.slice(1).toLowerCase()}` as
                | "claimsStatusPending"
                | "claimsStatusClaimed"
                | "claimsStatusRevoked"
                | "claimsStatusExpired",
            )}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("claimsTitle")}</h1>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder={t("claimsFilterAll")}
          options={[
            { value: "PENDING", label: t("claimsFilterPending") },
            { value: "CLAIMED", label: t("claimsFilterClaimed") },
            { value: "REVOKED", label: t("claimsFilterRevoked") },
            { value: "EXPIRED", label: t("claimsFilterExpired") },
          ]}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(r) => r.id}
        emptyText={t("claimsEmpty")}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
